import { Outlet, useParams } from "react-router";

import { ChatListPage } from "@/features/messaging/pages/ChatListPage";
import { cn } from "@/utils/cn";

/**
 * docs/ui/routes.md `/chat` and `/chat/:conversationId`: "On desktop this
 * renders as the detail pane of a split view alongside the `/chat` list,
 * not a full navigation away from it; on mobile it's a full-screen push."
 * This is the parent that makes that true — router.tsx nests both routes
 * under it, `:conversationId` as a relative child, so `useParams` here
 * picks up the child's param without this component needing its own route
 * segment for it.
 *
 * The list pane (`ChatListPage`) is rendered directly, not through
 * `Outlet` — it must stay mounted and visible across both `/chat` and
 * `/chat/:conversationId` so a live socket update to conversation N+1
 * doesn't get lost while conversation N's thread is open. Only the right
 * pane swaps, via `Outlet` (`ChatEmptyDetailPage` at the index route,
 * `ChatConversationPage` at `:conversationId`).
 *
 * Below `md`, only one pane shows at a time — the list until a conversation
 * is chosen, then the thread full-width, matching the doc's "full-screen
 * push." Above `md`, both panes are always visible.
 */
export function ChatPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();

  return (
    <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-[1280px]">
      <div
        className={cn(
          "w-full shrink-0 border-r border-border md:block md:w-[320px]",
          conversationId ? "hidden" : "block",
        )}
      >
        <ChatListPage selectedConversationId={conversationId} />
      </div>
      <div className={cn("min-w-0 flex-1", conversationId ? "block" : "hidden md:block")}>
        <Outlet />
      </div>
    </div>
  );
}
